import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { TranslationValues, useTranslations } from 'use-intl';

import { FormErrors, FundraisingDocumentType } from '@blockpulse3/data/shared';
import {
  IdentityType,
  MergedSubscriptionDetailsStep,
  SubscriptionWorkflowType,
  useGetSubscriptionDocumentQuery,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumber, formatNumberCurrency, getNearestMultiple } from '@blockpulse3/helpers';
import {
  ErrorMessage,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';

import { SubscriptionHoldingMethod } from '../../../../SubscriptionHoldingMethod';
import { FundsSourceUpload } from './FundsSourceUpload';
import { schema } from './schema';
import { SubscriptionDetailsForm } from './types';

type Props = {
  onClose: () => void;
  onSubmit: (data: SubscriptionDetailsForm) => void;
  subscriptionStep: MergedSubscriptionDetailsStep;
} & Omit<ResponsiveModalProps, 'children'>;

export function SubscriptionDetailsModal({
  onClose,
  onSubmit,
  subscriptionStep,
  ...props
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSubscription = useTranslations('Subscription');
  const i18nSubscriptionDetailsModal = useTranslations('SubscriptionDetailsModal');
  const i18nAlertAmount = useTranslations('AlertAmount');

  /* ** Track amount touched state ** */
  const [isAmountTouched, setIsAmountTouched] = useState<boolean>(false);
  const [errorValues, setErrorValues] = useState<TranslationValues | undefined>(undefined);

  const { subscriptionId = '' } = useParams();

  const { data: documentData } = useGetSubscriptionDocumentQuery({
    variables: { subscriptionId, documentType: FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE },
  });
  const fundsSourceCertificate = documentData?.getSubscriptionDocument?.document;

  const { data } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const subscription = data?.subscription;
  const fundraising = subscription?.fundraising;
  const isIndividual = subscription?.identity.type === IdentityType.INDIVIDUAL;

  const defaultValues: SubscriptionDetailsForm = useMemo(() => {
    return {
      investAmount: subscription?.investAmount || 0,
    };
  }, [subscription]);

  const methods = useForm<SubscriptionDetailsForm>({
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { register, formState, reset, handleSubmit, watch, setError } = methods;

  /* ** Reset form values with default parameters ** */
  useEffect(() => {
    reset({ ...defaultValues }, { keepErrors: true });
  }, [defaultValues, reset]);

  /* ** Compute amount to a multiple of the pricePerShare ** */
  const pricePerShare = subscription?.fundraising.pricePerShare || 1;
  const investAmount = new BigNumber(watch('investAmount'));
  const nearestMultiple = getNearestMultiple(investAmount, pricePerShare);
  const fixedAmount = nearestMultiple.isZero() ? 0 : nearestMultiple.toNumber();
  const isFixed = !investAmount.isEqualTo(fixedAmount);
  const isNull = nearestMultiple.isNaN();

  const hardCap = subscription?.fundraising.hardCap;
  const min = subscription?.minimalAmount || subscriptionStep?.minimalAmount || pricePerShare;
  let max = subscription?.maximalAmount || subscriptionStep?.maximalAmount || hardCap;
  if (
    fundraising?.subscriptionWorkflowType === SubscriptionWorkflowType.FIRST_IN &&
    max &&
    fundraising?.data.remainingSubscriptionAmount
  ) {
    max = Math.min(max, fundraising?.data.remainingSubscriptionAmount);
  }

  const showAlert =
    investAmount.isGreaterThanOrEqualTo(min || 0) &&
    investAmount.isLessThanOrEqualTo(max || 0) &&
    (isFixed || isNull) &&
    isAmountTouched;

  const mifidStep = subscription?.data.mergedSteps.find(
    (step) => step.__typename === 'MergedMifidStep',
  );
  const proofOfFundsSourceThreshold = subscriptionStep?.proofOfFundsSourceThreshold;
  const needsFundsSourceCertificate =
    !!mifidStep && proofOfFundsSourceThreshold && fixedAmount >= proofOfFundsSourceThreshold;

  const handleFormSubmit: SubmitHandler<SubscriptionDetailsForm> = (): void => {
    const proofOfFundsSourceThresholdFormatted = formatNumberCurrency(
      proofOfFundsSourceThreshold || 0,
    );

    // Show error and continue form validation
    if (needsFundsSourceCertificate && !fundsSourceCertificate) {
      setErrorValues({
        proofOfFundsSourceThreshold: proofOfFundsSourceThresholdFormatted,
      });
      setError('fundsSourceCertificate', {
        type: 'custom',
        message: FormErrors.FundsSourceCertificateMissing,
      });
    }

    if (investAmount.isLessThan(min)) {
      setErrorValues({
        minimalAmount: formatNumberCurrency(min),
        proofOfFundsSourceThreshold: proofOfFundsSourceThresholdFormatted,
      });
      setError('investAmount', { type: 'custom', message: FormErrors.MinimalAmount });
    } else if (max && (fixedAmount > max || investAmount.isGreaterThan(max))) {
      setErrorValues({
        maximalAmount: formatNumberCurrency(max),
        proofOfFundsSourceThreshold: proofOfFundsSourceThresholdFormatted,
      });
      setError('investAmount', { type: 'custom', message: FormErrors.MaximalAmount });
    } else {
      onSubmit({ investAmount: fixedAmount });
    }
  };

  const handleFormChange = (): void => {
    setIsAmountTouched(true);
  };

  return (
    <ResponsiveModal onClose={onClose} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="xl" p="4">
          {i18nSubscription('SupportSelectionWithAmount')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Stack}>
          <Stack>
            <FormProvider {...methods}>
              <form id="subscription-details" onSubmit={handleSubmit(handleFormSubmit)}>
                <FormControl
                  isInvalid={!!formState.errors.investAmount}
                  padding="0"
                  onChange={handleFormChange}
                >
                  <FormLabel fontSize="lg" fontWeight="600" htmlFor="investAmount">
                    {i18nSubscriptionDetailsModal('Title')}
                  </FormLabel>
                  <FormLabel color="grey.500" fontWeight="400" htmlFor="investAmount">
                    {min && min > 0 && min === max
                      ? i18nSubscriptionDetailsModal('AmountsAreEqualDescription', {
                          minimalAmount: formatNumber(min),
                        })
                      : i18nSubscriptionDetailsModal('AmountDescription', {
                          minimalAmount: formatNumber(min),
                          maximalAmount: formatNumber(max || 0),
                        })}
                    <br />
                    {i18nSubscriptionDetailsModal('Description')}
                  </FormLabel>
                  <Stack py="2">
                    <Text fontSize="lg" fontWeight="600">
                      {i18nSubscriptionDetailsModal('InvestingIntent')}
                    </Text>
                    <InputGroup>
                      <Input
                        id="investAmount"
                        step="0.01"
                        type="number"
                        {...register('investAmount')}
                      />
                      <InputRightAddon>€</InputRightAddon>
                    </InputGroup>
                    {showAlert && (
                      <Alert mt="3" status="warning">
                        <AlertIcon as={ExclamationIcon} />
                        <Stack spacing="1">
                          <AlertTitle>{i18nAlertAmount('Title')}</AlertTitle>
                          <AlertDescription>
                            {i18nAlertAmount('Description', {
                              fixedAmount: formatNumberCurrency(fixedAmount),
                            })}
                          </AlertDescription>
                        </Stack>
                      </Alert>
                    )}
                  </Stack>
                  <ErrorMessage error={formState.errors.investAmount} values={errorValues} />
                </FormControl>
                {needsFundsSourceCertificate && <FundsSourceUpload errorValues={errorValues} />}
              </form>
            </FormProvider>
            {isIndividual && <SubscriptionHoldingMethod />}
          </Stack>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            {i18nGeneral('Cancel')}
          </Button>
          <Button form="subscription-details" type="submit">
            {i18nGeneral('Submit')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}
